import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAlert } from '../Components/AlertContext';
import { mapVoiceOptions, VoiceOption } from '../Pages/VoiceProfileHelpers';
import { useVoiceProfileApi } from '../Services/VoiceProfileApiProvider';
import { VoiceOptionApi } from '../Services/VoiceProfileApiProviderTypes';

/**
 * The voices on offer. `selectedVoice` stays in the list even when the list
 * does not hold it, so that an already saved voice is not dropped.
 * `refresh` asks the backend to reload the voices from ElevenLabs instead of
 * serving them from its cache.
 */
export function useVoiceOptions(selectedVoice?: string, enabled: boolean = true) {
  const { getVoiceOptions } = useVoiceProfileApi();
  const { showAlert } = useAlert();

  const [items, setItems] = useState<VoiceOptionApi[]>([]);
  const [loading, setLoading] = useState(false);
  // Only the latest request may fill the list: an older, slower one is dropped.
  const latestRequest = useRef(0);

  const load = useCallback(
    async (forceRefresh: boolean) => {
      const request = ++latestRequest.current;
      setLoading(true);
      try {
        const data = await getVoiceOptions({ forceRefresh });
        if (request === latestRequest.current) setItems(data.items);
        return true;
      } catch (error) {
        if (request === latestRequest.current) {
          if (!forceRefresh) setItems([]);
          showAlert('fail', forceRefresh ? 'Failed to reload voices' : 'Failed to load voices');
        }
        return false;
      } finally {
        if (request === latestRequest.current) setLoading(false);
      }
    },
    [getVoiceOptions, showAlert],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    load(false);
    return () => {
      latestRequest.current++;
    };
  }, [enabled]);

  const refresh = useCallback(async () => {
    if (await load(true)) showAlert('success', 'Voices were reloaded');
  }, [load, showAlert]);

  const voices: VoiceOption[] = useMemo(
    () => mapVoiceOptions(items, selectedVoice),
    [items, selectedVoice],
  );

  return { voices, loading, refresh };
}
